import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Tooltip,
  IconButton,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { t } from "ttag";
import { useStoreActions } from "../../models/RootStore";
import ServiceNavigationBar from "../../widgets/Menu/ServiceNavigationBar";
import useStyles from "./NGDevices.style";
import { ContentCopy } from "@mui/icons-material";
import {
  ConnectedIcon,
  DisconnectedIcon,
  FetchingIpIcon,
  VerifyingIpIcon,
  VerifyingAltIpIcon,
  ConnectedUnreachableIcon
} from "./StatusIcons";

const extractIpFromCommandResponse = (res: any): string => {
  if (!res) {
    return "";
  }
  let text = "";
  if (typeof res === "string") {
    text = res;
  } else if (typeof res === "object") {
    text = res.result || res.output || res.stdout || res.response || res.text || JSON.stringify(res);
  }

  if (!text) {
    return "";
  }

  // Split lines to look for inet blocks
  const lines = text.split("\n");
  for (const line of lines) {
    const match = line.match(/inet\s+(?:addr:)?([0-9.]+)/i);
    if (match && match[1]) {
      const ip = match[1].trim();
      if (ip !== "127.0.0.1") {
        return ip;
      }
    }
  }

  // Fallback regex to match any IPv4 pattern that is not loopback/mask/broadcast
  const allIps = text.match(/\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/g) || [];
  for (const ip of allIps) {
    if (ip !== "127.0.0.1" && !ip.endsWith(".255") && !ip.startsWith("255.")) {
      return ip;
    }
  }
  return "";
};

const getCachedIps = (): Record<string, any> => {
  try {
    return JSON.parse(localStorage.getItem("ng_device_ips") || "{}");
  } catch (e) {
    return {};
  }
};

const saveCachedIps = (ips: Record<string, any>) => {
  localStorage.setItem("ng_device_ips", JSON.stringify(ips));
};

const checkIpReachable = async (ip: string, port?: string, path: string = "/control/login"): Promise<boolean> => {
  if (!ip) {
    return false;
  }
  const targetUrl = `http://${ip}${port ? `:${port}` : ""}${path}`;

  // If running on HTTP (local development), perform a direct browser fetch to avoid backend false positives
  if (window.location.protocol === "http:") {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, 2000);
    try {
      await fetch(targetUrl, { signal: controller.signal, mode: "no-cors" });
      clearTimeout(timeoutId);
      return true;
    } catch (e) {
      clearTimeout(timeoutId);
      return false;
    }
  }

  // Otherwise (HTTPS / production), route through the devServer proxy-check
  const url = `/proxy-check?url=${encodeURIComponent(targetUrl)}`;
  try {
    const res = await fetch(url);
    return res.status === 200;
  } catch (e) {
    return false;
  }
};

const sortDevices = (list: any[]): any[] => {
  const prioritySerials = ["283B96040048", "283B96040028"];

  return [...list].sort((a: any, b: any) => {
    const aSerial = a.serial || "";
    const bSerial = b.serial || "";

    const aPriorityIndex = prioritySerials.indexOf(aSerial);
    const bPriorityIndex = prioritySerials.indexOf(bSerial);

    // If both have priority, sort by their order in prioritySerials
    if (aPriorityIndex !== -1 && bPriorityIndex !== -1) {
      return aPriorityIndex - bPriorityIndex;
    }
    if (aPriorityIndex !== -1) {
      return -1;
    }
    if (bPriorityIndex !== -1) {
      return 1;
    }

    // Otherwise sort by connected status (connected first)
    const aConnected = !!a.ip;
    const bConnected = !!b.ip;

    if (aConnected && !bConnected) {
      return -1;
    }
    if (!aConnected && bConnected) {
      return 1;
    }

    // Fallback stable sorting by name/serial
    const aName = a.name || a.serial || "";
    const bName = b.name || b.serial || "";
    return aName.localeCompare(bName);
  });
};

const NGDevices = (props: any) => {
  const styles: any = useStyles();
  const [devices, setDevices] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const getDevicesBySerial = useStoreActions(
    (actions) => actions.devices.getDevicesBySerial
  );

  const controlDevice = useStoreActions(
    (actions) => actions.devices.controlDevice
  );

  const fetchNGDevices = () => {
    setLoading(true);
    setError(null);
    getDevicesBySerial({ serial: "283B9604" })
      .then((res: any) => {
        if (res) {
          // Parse dictionary shape to flat array
          const devicesList = Array.isArray(res)
            ? res
            : Object.values(res);

          const cachedIps = getCachedIps();

          // 1. Map cached IPs to devices
          const initialDevices = devicesList.map((device: any) => {
            const cacheEntry = device.id ? cachedIps[device.id] : null;
            let cachedIp = "";
            let cachedPort = "";
            if (cacheEntry) {
              if (typeof cacheEntry === "object") {
                cachedIp = cacheEntry.ip || "";
                cachedPort = cacheEntry.port || "";
              } else if (typeof cacheEntry === "string") {
                cachedIp = cacheEntry;
              }
            }
            return {
              ...device,
              ip: cachedIp,
              port: cachedPort || device.port || "",
              loadingIp: !cachedIp,
              updatingIp: false,
              isUnreachable: false,
              status: cachedIp ? "verifying_ip" : "fetching_ip",
            };
          });

          // Show the list immediately with cached data, sorted accordingly
          setDevices(sortDevices(initialDevices));
          setLoading(false);

          // Helper to process a single device asynchronously
          const processDevice = async (device: any, isSilentRefresh: boolean) => {
            if (!device.id) {
              return;
            }

            const updateDeviceState = (updatedFields: any) => {
              setDevices((prevDevices) => {
                const updated = prevDevices.map((d: any) =>
                  d.id === device.id ? { ...d, ...updatedFields } : d
                );
                return sortDevices(updated);
              });
            };

            if (isSilentRefresh) {
              updateDeviceState({ status: "verifying_ip", updatingIp: true });
            } else {
              updateDeviceState({ status: "fetching_ip", loadingIp: true });
            }

            let resolvedDevice = { ...device };

            try {
              const cmdRes = await controlDevice({
                deviceId: device.id,
                command: { command: "ifconfig" }
              });
              const extractedIp = extractIpFromCommandResponse(cmdRes);
              if (extractedIp) {
                const currentCached = getCachedIps();
                currentCached[device.id] = { ip: extractedIp, port: device.port || null };
                saveCachedIps(currentCached);

                resolvedDevice.ip = extractedIp;
                resolvedDevice.loadingIp = false;
                resolvedDevice.updatingIp = false;
              } else {
                resolvedDevice.loadingIp = false;
                resolvedDevice.updatingIp = false;
              }
            } catch (err) {
              console.error(`Failed to fetch IP for device ${device.id}:`, err);
              resolvedDevice.loadingIp = false;
              resolvedDevice.updatingIp = false;
            }

            // Update UI with fetched IP
            updateDeviceState({
              ...resolvedDevice,
              status: resolvedDevice.ip ? "verifying_ip" : "disconnected"
            });

            // Reachability Check & Fallbacks
            if (device.serial === "283B96040048" || device.serial === "283B96040028") {
              const fallbackIp = "51.84.116.60";
              const fallbackPort = device.serial === "283B96040048" ? "10048" : "10028";
              const path = device.serial === "283B96040028" ? "/control/site" : "/control/login";
              const fallbackLoginUrl = `http://${fallbackIp}:${fallbackPort}${path}`;

              if (resolvedDevice.ip) {
                updateDeviceState({ status: "verifying_ip", ip: resolvedDevice.ip, loadingIp: false, updatingIp: true });
                const isReachable = await checkIpReachable(resolvedDevice.ip, resolvedDevice.port, path);
                if (isReachable) {
                  updateDeviceState({
                    ip: resolvedDevice.ip,
                    status: "connected",
                    isUnreachable: false,
                    updatingIp: false,
                    loadingIp: false,
                  });
                  return;
                }

                updateDeviceState({ status: "verifying_alt_ip", updatingIp: true });
                const isFallbackReachable = await checkIpReachable(fallbackIp, fallbackPort, path);
                if (isFallbackReachable) {
                  const currentCached = getCachedIps();
                  currentCached[device.id] = { ip: fallbackIp, port: fallbackPort };
                  saveCachedIps(currentCached);

                  updateDeviceState({
                    ip: fallbackIp,
                    port: fallbackPort,
                    loginUrl: fallbackLoginUrl,
                    status: "connected",
                    isUnreachable: false,
                    updatingIp: false,
                    loadingIp: false,
                  });
                  return;
                }

                updateDeviceState({
                  ip: fallbackIp,
                  port: fallbackPort,
                  loginUrl: fallbackLoginUrl,
                  status: "connected_unreachable",
                  isUnreachable: true,
                  updatingIp: false,
                  loadingIp: false,
                });
              } else {
                updateDeviceState({ status: "verifying_alt_ip", updatingIp: true });
                const isFallbackReachable = await checkIpReachable(fallbackIp, fallbackPort, path);
                if (isFallbackReachable) {
                  const currentCached = getCachedIps();
                  currentCached[device.id] = { ip: fallbackIp, port: fallbackPort };
                  saveCachedIps(currentCached);

                  updateDeviceState({
                    ip: fallbackIp,
                    port: fallbackPort,
                    loginUrl: fallbackLoginUrl,
                    status: "connected",
                    isUnreachable: false,
                    updatingIp: false,
                    loadingIp: false,
                  });
                  return;
                }

                updateDeviceState({
                  status: "disconnected",
                  isUnreachable: false,
                  updatingIp: false,
                  loadingIp: false,
                });
              }
            } else {
              // For normal devices, run reachability check if resolved
              if (resolvedDevice.ip) {
                updateDeviceState({ status: "verifying_ip", ip: resolvedDevice.ip, loadingIp: false, updatingIp: true });
                const isReachable = await checkIpReachable(resolvedDevice.ip, resolvedDevice.port, "/control/login");
                updateDeviceState({
                  status: isReachable ? "connected" : "connected_unreachable",
                  isUnreachable: !isReachable,
                  updatingIp: false,
                  loadingIp: false,
                });
              } else {
                updateDeviceState({
                  status: "disconnected",
                  isUnreachable: false,
                  updatingIp: false,
                  loadingIp: false,
                });
              }
            }
          };

          // Start processing all devices in parallel
          initialDevices.forEach((device: any) => {
            const isCached = !!device.ip;
            processDevice(device, isCached);
          });
        } else {
          setDevices([]);
        }
      })
      .catch((err: any) => {
        console.error("Error fetching NG devices:", err);
        setError(err?.message || t`Failed to fetch NG devices`);
        setDevices([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchNGDevices();
  }, []);

  const getStatusText = (device: any) => {
    switch (device.status) {
      case "connected":
        return t`Connected`;
      case "disconnected":
        return t`Disconnected`;
      case "connected_unreachable":
        return t`Connected but unreachable`;
      case "fetching_ip":
        return t`Fetching IP`;
      case "verifying_ip":
        return t`Verifying IP`;
      case "verifying_alt_ip":
        return t`Verifying Alternative IP`;
      default:
        if (device.loadingIp && !device.ip) {
          return t`Fetching IP`;
        }
        if (device.isUnreachable) {
          return t`Connected but unreachable`;
        }
        return device.ip ? t`Connected` : t`Disconnected`;
    }
  };

  const getStatusColor = (device: any) => {
    switch (device.status) {
      case "connected":
        return "#2e7d32";
      case "disconnected":
        return "#d32f2f";
      case "connected_unreachable":
        return "#ed6c02";
      case "fetching_ip":
      case "verifying_ip":
      case "verifying_alt_ip":
        return "#0288d1";
      default:
        if (device.loadingIp && !device.ip) {
          return "#0288d1";
        }
        if (device.isUnreachable) {
          return "#ed6c02";
        }
        return device.ip ? "#2e7d32" : "#d32f2f";
    }
  };

  const renderStatusIcon = (status: string) => {
    switch (status) {
      case "connected":
        return <ConnectedIcon />;
      case "disconnected":
        return <DisconnectedIcon />;
      case "connected_unreachable":
        return <ConnectedUnreachableIcon />;
      case "fetching_ip":
        return <FetchingIpIcon />;
      case "verifying_ip":
        return <VerifyingIpIcon />;
      case "verifying_alt_ip":
        return <VerifyingAltIpIcon />;
      default:
        return null;
    }
  };

  const handleConnect = (device: any) => {
    let url = "";
    const isPriority = device.serial === "283B96040048" || device.serial === "283B96040028";

    if (isPriority) {
      const fallbackIp = "51.84.116.60";
      const fallbackPort = device.serial === "283B96040048" ? "10048" : "10028";
      const path = device.serial === "283B96040028" ? "/control/site" : "/control/login";

      if (device.ip === fallbackIp) {
        url = `http://${fallbackIp}:${fallbackPort}${path}`;
      } else {
        url = device.ip ? `http://${device.ip}${path}` : "";
      }
    } else {
      url = device.loginUrl || (device.ip ? `http://${device.ip}/control/login` : "");
    }

    if (url) {
      const win = window.open(url, "_blank");
      win?.focus();
    } else {
      alert(t`No IP address available for this device`);
    }
  };

  const handleConnectLocally = (device: any) => {
    if (!device.ip) {
      alert(t`No IP address available for local connection`);
      return;
    }
    const localUrl = `http://localhost:3000/control/login?ng-server-ip=${device.ip}${device.port ? `&ng-server-port=${device.port}` : ""}`;
    const win = window.open(localUrl, "_blank");
    win?.focus();
  };

  return (
    <>
      <ServiceNavigationBar title={t`NG Devices`} {...props}>
        <Paper elevation={0} className={styles.paperPadding}>
          {loading && devices.length === 0 ? (
            <div className={styles.noDataMessage}>{t`Loading...`}</div>
          ) : error ? (
            <div className={styles.noDataMessage} style={{ color: "#d32f2f" }}>{error}</div>
          ) : devices.length === 0 ? (
            <div className={styles.noDataMessage}>{t`No devices found`}</div>
          ) : (
            <TableContainer>
              <Table stickyHeader aria-label="NG devices table">
                <TableHead className={styles.tableHead}>
                  <TableRow>
                    <TableCell className={styles.headCells}>{t`NAME`}</TableCell>
                    <TableCell className={styles.headCells}>{t`STATUS`}</TableCell>
                    <TableCell className={styles.headCells}>{t`IP ADDRESS`}</TableCell>
                    <TableCell align="center" className={styles.headCells}>{t`CONNECT`}</TableCell>
                    <TableCell align="center" className={styles.headCells}>{t`CONNECT LOCALLY`}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {devices.map((device: any, index: number) => {
                    const deviceName = device.name || device.serial || `Device ${index + 1}`;
                    const isConnected = !!device.ip && !device.isUnreachable;
                    const buttonTooltip = device.isUnreachable
                      ? t`device is connected but not reachable from your location (require VPN)`
                      : t`No IP address available. Device is disconnected.`;

                    return (
                      <TableRow
                        hover
                        tabIndex={-1}
                        key={device.serial || index}
                        onDoubleClick={() => {
                          if (device.id) {
                            props.history.push(`/devices/${device.id}`);
                          }
                        }}
                        style={{ cursor: "pointer" }}
                      >
                        <TableCell component="th" scope="row" align="left" className={styles.rowCell}>
                          {deviceName}
                        </TableCell>
                        <TableCell align="left" className={styles.rowCell}>
                          {device.status === "connected_unreachable" ? (
                            <Tooltip title={t`device is connected but not reachable from your location (require VPN)`}>
                              <div style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "help" }}>
                                <ConnectedUnreachableIcon />
                                <span className={styles.statusText} style={{ color: getStatusColor(device) }}>
                                  {getStatusText(device)}
                                </span>
                              </div>
                            </Tooltip>
                          ) : (
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              {renderStatusIcon(device.status)}
                              <span className={styles.statusText} style={{ color: getStatusColor(device) }}>
                                {getStatusText(device)}
                              </span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell align="left" className={styles.rowCell}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            {device.ip && (
                              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                <span>{device.ip}{device.port ? `:${device.port}` : ""}</span>
                                <Tooltip title={t`Copy IP address`}>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigator.clipboard.writeText(`${device.ip}${device.port ? `:${device.port}` : ""}`);
                                    }}
                                    style={{ padding: 4 }}
                                  >
                                    <ContentCopy style={{ fontSize: 14 }} />
                                  </IconButton>
                                </Tooltip>
                              </div>
                            )}
                            {device.loadingIp && (
                              <CircularProgress size={16} className={styles.loaderStyle} />
                            )}
                            {device.updatingIp && (
                              <CircularProgress size={16} className={styles.loaderStyle} />
                            )}
                            {!device.ip && !device.loadingIp && !device.updatingIp && <span>-</span>}
                          </div>
                        </TableCell>
                        <TableCell align="center" className={styles.rowCell}>
                          <Tooltip title={isConnected ? "" : buttonTooltip}>
                            <span>
                              <Button
                                size="small"
                                disabled={!isConnected}
                                className={styles.connectButton}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleConnect(device);
                                }}
                              >
                                {t`Connect`}
                              </Button>
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center" className={styles.rowCell}>
                          <Tooltip title={isConnected ? t`Make sure to run your Coolremote-client repo using the \`npm run start-ng\` and approve the SDK rebuild in NG mode before clicking this button` : buttonTooltip}>
                            <span>
                              <Button
                                size="small"
                                disabled={!isConnected}
                                className={styles.connectLocallyButton}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleConnectLocally(device);
                                }}
                              >
                                {t`Connect Locally`}
                              </Button>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </ServiceNavigationBar>
    </>
  );
};

export default NGDevices;
